import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from "react";

import { getData, setData } from "../utils";

export interface ILayoutConfig {
  name: string;
  options: Record<string, unknown>; // instead of any, more generic
}

export interface ILayoutProvider {
  layout: ILayoutConfig;
}

export interface ILayoutProps {
  getLayout: (name: string) => Partial<ILayoutConfig> | undefined;
  hasLayout: (name: string) => boolean;
  updateLayout: (name: string, config: Partial<ILayoutConfig>) => void;
  currentLayout: Partial<ILayoutConfig> | null;
  setCurrentLayout: (layoutProvider: Partial<ILayoutConfig> | null) => void;
}

const LAYOUTS_CONFIGS_KEY = "layouts-configs";

const getLayouts = (): Map<string, Partial<ILayoutConfig>> => {
  const storedLayouts = (getData(LAYOUTS_CONFIGS_KEY) as Record<string, Partial<ILayoutConfig>>) || {};
  return new Map(Object.entries(storedLayouts));
};

const initialProps: ILayoutProps = {
  getLayout: (_name: string): Partial<ILayoutConfig> | undefined => {
    void _name; // silence unused param warning
    return undefined;
  },
  hasLayout: (_name: string): boolean => {
    void _name;
    return false;
  },
  updateLayout: (_name: string, _config: Partial<ILayoutConfig>) => {
    void _name;
    void _config;
  },
  currentLayout: null,
  setCurrentLayout: (_layoutProvider: Partial<ILayoutConfig> | null) => {
    void _layoutProvider;
  },
};


const LayoutContext = createContext<ILayoutProps>(initialProps);
const useLayout = () => useContext(LayoutContext);

const LayoutProvider = ({ children }: PropsWithChildren) => {
  const getLayout = (name: string): Partial<ILayoutConfig> | undefined => {
    const storedLayouts = getLayouts();
    return storedLayouts.get(name);
  };

  const hasLayout = (name: string): boolean => {
    const storedLayouts = getLayouts();
    return storedLayouts.has(name);
  };

  const updateLayout = (name: string, config: Partial<ILayoutConfig>) => {
    const storedLayouts = getLayouts();

    if (storedLayouts.has(name)) {
      storedLayouts.delete(name);
    }

    storedLayouts.set(name, config);

    setData(LAYOUTS_CONFIGS_KEY, Object.fromEntries(storedLayouts));
  };

  const [currentLayout, setCurrentLayout] = useState<Partial<ILayoutConfig> | null>(null);

  return (
    <LayoutContext.Provider
      value={{
        getLayout,
        hasLayout,
        updateLayout,
        currentLayout,
        setCurrentLayout,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export { LayoutContext, LayoutProvider, useLayout };
